import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, CalendarClock, CheckCircle2, CreditCard, FileText, RefreshCw, ShieldCheck, Users } from 'lucide-react'

import { useAuth } from '@/contexts/AuthContext'
import { getTenantService } from '@/services/tenant'
import { getPaymentService } from '@/services/payments'
import { openRazorpayCheckout } from '@/services/payments/razorpayCheckout'
import { toast } from '@/store/toastStore'
import type { BillingCycle, BillingOverview, PlanCode, SubscriptionCommandResult, SubscriptionInvoice } from '@/types/tenant.types'
import { getPlanDisplayName } from '@/types/tenant.types'

const paidPlans: Exclude<PlanCode, 'trial'>[] = ['starter', 'professional', 'enterprise']

function operationKey(action: string): string {
  return `${action}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function displayDate(value?: string): string {
  return value ? new Date(value).toLocaleDateString('en-IN') : 'Not scheduled'
}

function displayAmount(total?: number, currency?: string): string {
  if (total == null || !currency) return 'Awaiting verified provider amount'
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(total / 100)
}

export default function BillingPage() {
  const { user } = useAuth()
  const [overview, setOverview] = useState<BillingOverview | null>(null)
  const [planCode, setPlanCode] = useState<Exclude<PlanCode, 'trial'>>('professional')
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('yearly')
  const [working, setWorking] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!user?.tenantId) return
    try {
      setError(null)
      const data = await getTenantService().getBillingOverview(user.tenantId)
      setOverview(data)
      if (data.subscription?.planCode !== 'trial') setPlanCode(data.subscription?.planCode ?? 'professional')
      setBillingCycle(data.subscription?.billingCycle ?? 'yearly')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Billing information could not be loaded.')
    }
  }, [user?.tenantId])

  useEffect(() => { void load() }, [load])

  async function collectInvoice(invoice: SubscriptionInvoice) {
    try {
      const order = await getPaymentService().createSubscriptionOrder(
        invoice.invoiceId,
        operationKey('subscription-checkout')
      )
      const proof = await openRazorpayCheckout(order)
      if (!proof) {
        toast.info('Checkout closed', 'The invoice remains pending and can be paid later.')
        return
      }
      const settlement = await getPaymentService().verifySubscriptionPayment(proof)
      if (settlement.billingDeliveryStatus === 'completed') {
        toast.success('Subscription payment confirmed', 'The verified plan transition has been applied.')
      } else {
        toast.info(
          'Payment captured, confirmation processing',
          'Provider settlement is safe; Tax Hive will apply it through the retry ledger.'
        )
      }
    } catch (err) {
      toast.error(
        'Subscription checkout unavailable',
        err instanceof Error ? err.message : 'The invoice remains pending. Please try again.'
      )
    }
  }

  async function retryInvoice(invoice: SubscriptionInvoice) {
    setWorking(`invoice-${invoice.invoiceId}`)
    setError(null)
    await collectInvoice(invoice)
    await load()
    setWorking(null)
  }

  async function run(action: string, operation: () => Promise<SubscriptionCommandResult>) {
    setWorking(action)
    setError(null)
    try {
      const result = await operation()
      if (result.downgradeBlockers.length) {
        toast.warning('Downgrade needs attention', 'Reduce the listed usage before the lower plan can take effect.')
      } else if (result.invoice?.status === 'pending') {
        toast.success('Billing request created', 'Opening secure Razorpay Checkout.')
        await collectInvoice(result.invoice)
      } else {
        toast.success('Subscription updated', 'The lifecycle request was recorded.')
      }
      await load()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Subscription request failed.'
      setError(message)
      toast.error('Subscription request failed', message)
    } finally {
      setWorking(null)
    }
  }

  if (!user) return null
  if (!overview) {
    return <div className="card card-elevated p-8">{error ?? 'Loading subscription workspace...'}</div>
  }

  const sub = overview.subscription
  const usable = sub && ['active', 'trial', 'grace'].includes(sub.status)
  const ended = sub && ['suspended', 'cancelled', 'expired', 'pending'].includes(sub.status)
  const activeSeats = overview.usage.users ?? 0
  const maxSeats = overview.currentLimits.maxUsers ?? 0

  return (
    <div className="space-y-6">
      <header className="relative overflow-hidden rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-teal-50 p-7 dark:border-amber-900/50 dark:from-amber-950/30 dark:via-surface-card dark:to-teal-950/30">
        <div className="absolute -right-16 -top-20 h-48 w-48 rounded-full bg-amber-300/20 blur-3xl" />
        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-amber-700 dark:text-amber-300">
              <ShieldCheck className="h-4 w-4" /> Owner controlled
            </div>
            <h1 className="text-3xl font-bold text-text-primary">Subscription and billing</h1>
            <p className="mt-2 max-w-2xl text-text-secondary">Manage plan lifecycle, invoices, and payment evidence without exposing billing authority to delegated administrators.</p>
          </div>
          <div className="rounded-2xl border border-surface-overlay bg-surface-card/90 px-5 py-4 shadow-sm">
            <p className="text-xs uppercase tracking-wider text-text-tertiary">Current plan</p>
            <p className="mt-1 text-xl font-bold text-text-primary">{sub ? getPlanDisplayName(sub.planCode) : 'No subscription'}</p>
            <p className="text-sm capitalize text-text-secondary">{sub?.status ?? 'Not started'}</p>
          </div>
        </div>
      </header>

      {error && <div className="alert alert-error"><AlertTriangle className="h-5 w-5" /><span>{error}</span></div>}
      {sub?.status === 'grace' && <div className="alert alert-warning"><CalendarClock className="h-5 w-5" /><span>Payment grace ends {displayDate(sub.graceEndsAt)}. Current plan access remains available only until then.</span></div>}
      {sub?.cancelAtPeriodEnd && <div className="alert alert-warning"><CalendarClock className="h-5 w-5" /><span>Cancellation is scheduled for {displayDate(sub.expiresAt)}. Paid access remains available until that date.</span></div>}
      {sub?.pendingChangeStatus === 'awaiting_payment' && <div className="alert alert-info"><CreditCard className="h-5 w-5" /><span>Awaiting verified payment. No plan change is applied from browser state alone.</span></div>}

      <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <section className="card card-elevated p-6">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-teal-100 p-3 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300"><Users className="h-5 w-5" /></div>
            <div><h2 className="text-lg font-semibold text-text-primary">Plan capacity</h2><p className="text-sm text-text-secondary">Only active users consume seats.</p></div>
          </div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl bg-surface-overlay/60 p-4"><p className="text-sm text-text-secondary">Active user seats</p><p className="mt-1 text-2xl font-bold text-text-primary">{activeSeats} / {maxSeats}</p></div>
            <div className="rounded-2xl bg-surface-overlay/60 p-4"><p className="text-sm text-text-secondary">Clients</p><p className="mt-1 text-2xl font-bold text-text-primary">{overview.usage.clients ?? 0} / {overview.currentLimits.maxClients ?? 0}</p></div>
          </div>
          <p className="mt-4 text-xs text-text-tertiary">Deactivation releases a seat. Reactivation reserves a seat and is denied if the active-seat limit is full.</p>
        </section>

        <section className="card card-elevated p-6">
          <h2 className="text-lg font-semibold text-text-primary">Plan request</h2>
          <p className="mt-1 text-sm text-text-secondary">Pricing and collection are confirmed by the configured payment provider, never by this form.</p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-medium text-text-primary">Plan<select aria-label="Plan" className="select select-bordered mt-2 w-full" value={planCode} onChange={(event) => setPlanCode(event.target.value as Exclude<PlanCode, 'trial'>)}>{paidPlans.map((plan) => <option key={plan} value={plan}>{getPlanDisplayName(plan)}</option>)}</select></label>
            <label className="text-sm font-medium text-text-primary">Billing cycle<select aria-label="Billing cycle" className="select select-bordered mt-2 w-full" value={billingCycle} onChange={(event) => setBillingCycle(event.target.value as BillingCycle)}><option value="monthly">Monthly</option><option value="yearly">Yearly</option></select></label>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            {!sub && <button className="btn btn-primary" disabled={!!working} onClick={() => void run('purchase', () => getTenantService().purchaseSubscription(user.tenantId, { planCode, billingCycle, idempotencyKey: operationKey('purchase') }))}>Start purchase</button>}
            {sub && usable && <button className="btn btn-primary" disabled={!!working} onClick={() => void run('change', () => getTenantService().changeSubscriptionPlan(user.tenantId, { planCode, billingCycle, idempotencyKey: operationKey('change') }))}>Request plan change</button>}
            {sub && usable && <button className="btn btn-outline" disabled={!!working} onClick={() => void run('renew', () => getTenantService().renewSubscription(user.tenantId, { billingCycle, idempotencyKey: operationKey('renew') }))}>Renew</button>}
            {sub && usable && !sub.cancelAtPeriodEnd && <button className="btn btn-ghost text-red-600" disabled={!!working} onClick={() => void run('cancel', () => getTenantService().cancelSubscription(user.tenantId, { reason: 'Owner requested cancellation', idempotencyKey: operationKey('cancel') }))}>Cancel at period end</button>}
            {sub && (ended || sub.cancelAtPeriodEnd) && <button className="btn btn-outline" disabled={!!working} onClick={() => void run('reactivate', () => getTenantService().reactivateSubscription(user.tenantId, { billingCycle, idempotencyKey: operationKey('reactivate') }))}><RefreshCw className="h-4 w-4" /> Reactivate</button>}
          </div>
        </section>
      </div>

      {overview.downgradeBlockers.length > 0 && <section className="rounded-2xl border border-orange-300 bg-orange-50 p-6 dark:border-orange-900 dark:bg-orange-950/30"><h2 className="flex items-center gap-2 text-lg font-semibold text-orange-900 dark:text-orange-200"><AlertTriangle className="h-5 w-5" /> Downgrade blocked by current usage</h2><div className="mt-4 grid gap-3 sm:grid-cols-2">{overview.downgradeBlockers.map((blocker) => <div key={blocker.quotaKey} className="rounded-xl bg-white/80 p-4 dark:bg-black/20"><p className="font-medium capitalize text-text-primary">{blocker.description}</p><p className="text-sm text-text-secondary">Used {blocker.used}, target {blocker.targetLimit}, reduce by {blocker.excess}.</p></div>)}</div></section>}

      <section className="card card-elevated overflow-hidden">
        <div className="flex items-center gap-3 border-b border-surface-overlay p-6"><FileText className="h-5 w-5 text-amber-600" /><div><h2 className="text-lg font-semibold text-text-primary">Invoices and receipts</h2><p className="text-sm text-text-secondary">Provider-confirmed amounts and payment evidence.</p></div></div>
        {overview.invoices.length === 0 ? <p className="p-6 text-sm text-text-secondary">No subscription invoices yet.</p> : <div className="overflow-x-auto"><table className="table"><thead><tr><th>Invoice</th><th>Purpose</th><th>Status</th><th>Amount</th><th>Created</th><th className="text-right">Action</th></tr></thead><tbody>{overview.invoices.map((invoice) => <tr key={invoice.invoiceId}><td className="font-mono text-xs">{invoice.invoiceNumber}</td><td className="capitalize">{invoice.purpose.replace('_', ' ')}</td><td><span className={`badge ${invoice.status === 'paid' ? 'badge-success' : invoice.status === 'failed' ? 'badge-error' : 'badge-warning'}`}>{invoice.status}</span></td><td>{displayAmount(invoice.totalMinor, invoice.currency)}</td><td>{displayDate(invoice.createdAt)}</td><td className="text-right">{invoice.status === 'pending' ? <button className="btn btn-sm btn-outline" disabled={!!working} onClick={() => void retryInvoice(invoice)}>{working === `invoice-${invoice.invoiceId}` ? <span className="loading loading-spinner loading-xs" /> : <CreditCard className="h-4 w-4" />} Pay now</button> : <span className="text-text-tertiary">-</span>}</td></tr>)}</tbody></table></div>}
      </section>

      <section className="card card-elevated p-6">
        <div className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-teal-600" /><div><h2 className="text-lg font-semibold text-text-primary">Lifecycle history</h2><p className="text-sm text-text-secondary">Immutable owner, provider, and system events.</p></div></div>
        <div className="mt-5 space-y-3">{overview.history.length === 0 ? <p className="text-sm text-text-secondary">No lifecycle events yet.</p> : overview.history.map((event) => <div key={event.eventId} className="flex flex-col justify-between gap-2 rounded-xl border border-surface-overlay p-4 sm:flex-row"><div><p className="font-medium text-text-primary">{event.eventType.replace(/\./g, ' ').replace(/_/g, ' ')}</p><p className="text-xs uppercase tracking-wide text-text-tertiary">{event.source}</p></div><time className="text-sm text-text-secondary">{displayDate(event.createdAt)}</time></div>)}</div>
      </section>
    </div>
  )
}
